import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["value", "disabled"]
const _hoisted_2 = ["value", "disabled"]
const _hoisted_3 = ["value", "disabled"]
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["value", "disabled", "min", "max"]

import { ref, watch } from 'vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'YmdDateInput',
  props: {
    modelValue: { default: '' },
    disabled: { type: Boolean, default: false },
    picker: { type: Boolean, default: true },
    min: {},
    max: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const yearRef = ref<HTMLInputElement | null>(null)
const monthRef = ref<HTMLInputElement | null>(null)
const dayRef = ref<HTMLInputElement | null>(null)
const pickerRef = ref<HTMLInputElement | null>(null)

const openPicker = () => {
  if (props.disabled) return
  const el = pickerRef.value
  if (!el) return
  try {
    if (typeof el.showPicker === 'function') {
      el.showPicker()
      return
    }
  } catch {
    // showPicker는 사용자 제스처 밖이면 실패할 수 있음 → 클릭으로 폴백
  }
  el.focus()
  el.click()
}

const localY = ref('')
const localM = ref('')
const localD = ref('')

const parseYmd = (raw?: string | null) => {
  const s = String(raw || '').trim()
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (!m) return { y: '', m: '', d: '' }
  return {
    y: m[1],
    m: m[2].padStart(2, '0'),
    d: m[3].padStart(2, '0'),
  }
}

const clampMonth = (raw: string) => {
  if (!raw) return ''
  const n = Math.min(12, Math.max(1, parseInt(raw, 10) || 0))
  return String(n).padStart(2, '0')
}

const clampDay = (raw: string, year: string, month: string) => {
  if (!raw) return ''
  let maxDay = 31
  if (year.length === 4 && month.length === 2) {
    const y = parseInt(year, 10)
    const m = parseInt(month, 10)
    if (y > 0 && m >= 1 && m <= 12) {
      maxDay = new Date(y, m, 0).getDate()
    }
  }
  const n = Math.min(maxDay, Math.max(1, parseInt(raw, 10) || 0))
  return String(n).padStart(2, '0')
}

const applyFromProp = () => {
  const p = parseYmd(props.modelValue)
  localY.value = p.y
  localM.value = p.m
  localD.value = p.d
}
applyFromProp()

watch(
  () => props.modelValue,
  () => {
    const ae = document.activeElement
    if (ae === yearRef.value || ae === monthRef.value || ae === dayRef.value) return
    applyFromProp()
  }
)

/** 월·일 한 자리(예: 1, 5)도 YYYY-MM-DD로 반영 */
const commit = (opts: { padIncomplete?: boolean } = {}) => {
  const pad = !!opts.padIncomplete
  let y = localY.value.replace(/\D/g, '').slice(0, 4)
  let m = localM.value.replace(/\D/g, '').slice(0, 2)
  let d = localD.value.replace(/\D/g, '').slice(0, 2)

  if (pad) {
    if (m.length === 1) m = clampMonth(m)
    if (d.length === 1) d = clampDay(d, y, m.length === 2 ? m : clampMonth(m))
    else if (d.length === 2 && y.length === 4 && m.length === 2) d = clampDay(d, y, m)
    if (m.length === 2) m = clampMonth(m)
  }

  localY.value = y
  localM.value = m
  localD.value = d

  if (!y && !m && !d) {
    if (props.modelValue) emit('update:modelValue', '')
    return
  }

  if (y.length === 4 && m.length === 2 && d.length === 2) {
    const next = `${y}-${m}-${d}`
    if (props.modelValue !== next) emit('update:modelValue', next)
  }
}

const onBlurPart = () => {
  commit({ padIncomplete: true })
}

const onYear = (e: Event) => {
  const el = e.target as HTMLInputElement
  const v = el.value.replace(/\D/g, '').slice(0, 4)
  localY.value = v
  el.value = v
  if (v.length === 4) monthRef.value?.focus()
  commit()
}

const onMonth = (e: Event) => {
  const el = e.target as HTMLInputElement
  let v = el.value.replace(/\D/g, '').slice(0, 2)
  // 2~9 입력 시 즉시 0N 패딩 (1월은 01 또는 10~12 대기)
  if (v.length === 1 && v >= '2' && v <= '9') {
    v = `0${v}`
  }
  localM.value = v
  el.value = v
  if (v.length === 2) {
    localM.value = clampMonth(v)
    el.value = localM.value
    dayRef.value?.focus()
  }
  commit()
}

const onDay = (e: Event) => {
  const el = e.target as HTMLInputElement
  let v = el.value.replace(/\D/g, '').slice(0, 2)
  if (v.length === 1 && v >= '4' && v <= '9') {
    v = `0${v}`
  }
  localD.value = v
  el.value = v
  if (v.length === 2) {
    const m = localM.value.length === 1 ? clampMonth(localM.value) : localM.value
    if (localM.value.length === 1) localM.value = m
    localD.value = clampDay(v, localY.value, m)
    el.value = localD.value
  }
  commit({ padIncomplete: v.length === 2 })
}

const onPicker = (e: Event) => {
  const v = (e.target as HTMLInputElement).value || ''
  if (v) {
    const p = parseYmd(v)
    localY.value = p.y
    localM.value = p.m
    localD.value = p.d
  } else {
    localY.value = ''
    localM.value = ''
    localD.value = ''
  }
  emit('update:modelValue', v)
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["ymd-date-input", { 'is-disabled': __props.disabled }])
  }, [
    _createElementVNode("input", {
      ref_key: "yearRef",
      ref: yearRef,
      class: "ymd-part ymd-year",
      type: "text",
      inputmode: "numeric",
      autocomplete: "off",
      placeholder: "YYYY",
      maxlength: "4",
      value: localY.value,
      disabled: __props.disabled,
      onInput: onYear,
      onBlur: onBlurPart
    }, null, 40, _hoisted_1),
    _cache[1] || (_cache[1] = _createElementVNode("span", { class: "ymd-sep" }, "-", -1)),
    _createElementVNode("input", {
      ref_key: "monthRef",
      ref: monthRef,
      class: "ymd-part ymd-month",
      type: "text",
      inputmode: "numeric",
      autocomplete: "off",
      placeholder: "MM",
      maxlength: "2",
      value: localM.value,
      disabled: __props.disabled,
      onInput: onMonth,
      onBlur: onBlurPart
    }, null, 40, _hoisted_2),
    _cache[2] || (_cache[2] = _createElementVNode("span", { class: "ymd-sep" }, "-", -1)),
    _createElementVNode("input", {
      ref_key: "dayRef",
      ref: dayRef,
      class: "ymd-part ymd-day",
      type: "text",
      inputmode: "numeric",
      autocomplete: "off",
      placeholder: "DD",
      maxlength: "2",
      value: localD.value,
      disabled: __props.disabled,
      onInput: onDay,
      onBlur: onBlurPart
    }, null, 40, _hoisted_3),
    (__props.picker)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "ymd-picker",
          title: "달력에서 선택",
          disabled: __props.disabled,
          onClick: openPicker
        }, [
          _createElementVNode("input", {
            ref_key: "pickerRef",
            ref: pickerRef,
            type: "date",
            class: "ymd-picker-native",
            tabindex: "-1",
            "aria-hidden": "true",
            value: __props.modelValue || '',
            disabled: __props.disabled,
            min: __props.min,
            max: __props.max,
            onChange: onPicker
          }, null, 40, _hoisted_5),
          _createVNode(_component_v_icon, { size: "16" }, {
            default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
              _createTextVNode("mdi-calendar", -1)
            ]))]),
            _: 1
          })
        ], 8, _hoisted_4))
      : _createCommentVNode("", true)
  ], 2))
}
}

})